import {
  Button,
  ButtonColor,
  ButtonSize,
  ButtonVariant,
  SimpleBlock,
  useFormContext,
  usePopupManager,
  useShowContext,
  Text,
} from 'ui-builder';
import {
  SendingStatus,
  SmsTask,
  TaskPermission,
  TaskType,
  useSendSmsTaskService,
} from 'nekst-api';
import React, { useState } from 'react';
import { Popup, WarningMessage } from 'features/nekst-widgets';
import { SendSmsPreview } from './SendSmsPreview';
import { Spacings } from 'react-native-ui-lib';
import { SmsTaskPreview } from './smsTaskPreviewService';

const SMS_CHARACTER_LIMIT = 320;

function SendSmsPreviewPopup(props: {
  task: SmsTask,
  onSent?: () => void,
}) {

  const popupManager = usePopupManager();
  const sendSmsService = useSendSmsTaskService();
  const [previews, setPreviews] = useState<SmsTaskPreview[]>([]);

  const sendSms = async () => {
    const result = await sendSmsService.sendSms(
      props.task.id,
    );

    if (result.sendingStatus === SendingStatus.SENT) {
      props.onSent?.();
    }

    popupManager.closePopup();
  };

  // Calculate the longest message length
  const longestMessageLength = previews.length > 0
    ? Math.max(...previews.map(p => p.characterCount))
    : 0;

  const isOverLimit = longestMessageLength > SMS_CHARACTER_LIMIT;

  const message = (
    <SimpleBlock
      style={{
        gap: Spacings.s4
      }}
    >
      {isOverLimit && (
        <WarningMessage>
          The SMS message exceeds the {SMS_CHARACTER_LIMIT} character limit
          (current: {longestMessageLength} characters). Please shorten your message before sending.
        </WarningMessage>
      )}
      <Text>Please review the sms message(s) preview below before sending:</Text>
      <SendSmsPreview task={props.task} onPreviewsLoaded={setPreviews} />
    </SimpleBlock>
  );
  return (
    <Popup
      title="Send SMS"
      content={message}
      buttons={(
        <Button
          text="Send"
          color={ButtonColor.GREEN}
          size={ButtonSize.MEDIUM}
          onClick={sendSms}
          isDisabled={isOverLimit}
        />
      )}
    />
  );
}

export function SendSmsButton(
  props: {
    className?: string
    addPreviewPrefix?: boolean,
  }
) {
  const popupManager = usePopupManager();

  const formContext = useFormContext<SmsTask>();
  const showContext = useShowContext<SmsTask>();

  const [isSent, setIsSent] = React.useState(false);

  const { data: formData } = formContext;

  const data = formData || showContext?.data;

  if (
    data?.id
    && data?.permissions.includes(TaskPermission.SEND_SMS)
    && data.type === TaskType.SMS
  ) {
    const isNotSent = (data! as SmsTask).sendingStatus === SendingStatus.NOT_SENT && !isSent;

    let buttonText;
    if (isNotSent) {
      buttonText = 'Send';
    } else {
      buttonText = 'Re-Send';
    }

    if (props.addPreviewPrefix) {
      buttonText = `Preview & ${buttonText}`;
    }

    return (
      <Button
        customClass={props.className}
        text={buttonText}
        color={isNotSent ? ButtonColor.GREEN : ButtonColor.YELLOW}
        variant={ButtonVariant.FILLED}
        size={ButtonSize.SMALL}
        onClick={async () => {
          if (formContext.submitForm) {
            const submitResult = await formContext.submitForm(false);
            if (!submitResult) {
              return;
            }
          }

          popupManager.openPopup(<SendSmsPreviewPopup task={data} onSent={() => setIsSent(true)} />, true);
        }}
      />
    );
  } else {
    return null;
  }
}
